/**
 * `client-steward-setup` (crossmarc/steward-app#1767): the firm's record that it set up a
 * sponsored Steward workspace for ONE client record, and where that setup stands. One row per
 * `advisor-client` record ({@link ADVISOR_CLIENT_TYPE_CODE}, `advisorClient.ts`), in the firm's
 * own workspace, written by the façade under the firm caller's own bearer token
 * (`services/steward-app-api/src/handlers/clientStewardSetup.ts`).
 *
 * ## Why a type of its own
 *
 * Setup used to live on the intake (`advisor-intake.clientWorkspace`), keyed by the submission.
 * #1767 moves it to the client profile, keyed by the client record, and a client may have no
 * submission at all (known only from the onboarding form, #1765). The `advisor-client` row
 * cannot carry it: nothing the façade holds can write that type. So this row carries the same
 * {@link IntakeClientWorkspaceLink} shape the intake did, unchanged, and the façade MIRRORS it
 * onto every submission stamped with the same `clientId`, so the readers of the intake's link
 * (`setupHasStarted` in the intake writer, the #1786 claim mailer, the intakes list) keep
 * working with no change of their own.
 *
 * ## Found by attribute, like `onboarding-mark`
 *
 * appcore mints the instance id, so the façade lists this firm's rows and matches on
 * `clientId`. Two concurrent FIRST setups can each see "no row" and each create one. That does
 * NOT make two workspaces: appcore's provision is idempotent on `sourceRef`, which is the
 * client id, so both calls get the SAME workspace back. What it can make is two rows naming
 * that one workspace. Every reader tolerates it ({@link pickClientStewardSetup} takes the
 * newest), and the client purge deletes every match.
 *
 * ## Schemaless, like `advisor-intake`
 *
 * `link` is a nested object, which appcore's flat strict-mode field validation cannot express.
 * {@link ClientStewardSetupAttributesSchema} is the validation authority, applied by the façade
 * before every write and on every read.
 */
import { z } from 'zod';
import { IntakeClientWorkspaceLinkSchema, type IntakeClientWorkspaceLink } from './intakeClientWorkspace.js';

export const CLIENT_STEWARD_SETUP_TYPE_CODE = 'client-steward-setup';

const id = z.string().min(1).max(200);

export const ClientStewardSetupAttributesSchema = z.strictObject({
  firmId: id,
  /** The `advisor-client` row this setup belongs to (that type's own appcore `instanceId`), and
   *  appcore's `sourceRef` for the setup. */
  clientId: id,
  link: IntakeClientWorkspaceLinkSchema,
});

export type ClientStewardSetupAttributes = z.infer<typeof ClientStewardSetupAttributesSchema>;

export function readClientStewardSetup(
  value: unknown,
): { kind: 'setup'; setup: ClientStewardSetupAttributes } | { kind: 'unreadable' } {
  const parsed = ClientStewardSetupAttributesSchema.safeParse(value);
  return parsed.success ? { kind: 'setup', setup: parsed.data } : { kind: 'unreadable' };
}

/**
 * The one setup row a reader should act on, from every readable row for one client: the one
 * with the latest `linkedAt` (a later setup after a cancel is the live one), ties broken by
 * instance id so every reader picks the same row. `undefined` when there is none.
 */
export function pickClientStewardSetup<T extends { instanceId: string; setup: ClientStewardSetupAttributes }>(
  rows: readonly T[],
): T | undefined {
  let best: T | undefined;
  for (const row of rows) {
    if (!best) { best = row; continue; }
    const a = row.setup.link.linkedAt;
    const b = best.setup.link.linkedAt;
    if (a > b || (a === b && row.instanceId > best.instanceId)) best = row;
  }
  return best;
}

/** Whether a link still holds a setup the family could use or is using. */
export function clientStewardSetupIsLive(link: IntakeClientWorkspaceLink | undefined): boolean {
  return link?.state === 'invited' || link?.state === 'active';
}
